import { DatePicker } from '@/components/date-picker'
import { Button } from '@journ/ui/button'
import { Input } from '@journ/ui/input'
import { Label } from '@journ/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@journ/ui/select'
import { SubmitButton } from '@journ/ui/submit-button'
import { useState } from 'react'
import { useIntlayer } from 'react-intlayer'
import { z } from '@/libs/zod'
import type { TeamMember } from '../domain/member'
import type { TimeOffType } from '../domain/timeoff'

const schema = z
	.object({
		memberId: z.string().min(1),
		type: z.enum(['vacation', 'sick_leave', 'unpaid_leave', 'parental_leave', 'other']),
		startDate: z.string().min(1),
		endDate: z.string().min(1),
		halfDay: z.enum(['am', 'pm']).nullable().optional(),
		reason: z.string().optional(),
	})
	.refine(d => d.endDate >= d.startDate, { path: ['endDate'], message: 'End date must be after start date.' })

type TimeOffFormValues = z.infer<typeof schema>

type Props = {
	members: TeamMember[]
	showMember?: boolean
	initialMemberId?: string
	onSubmit: (values: TimeOffFormValues) => Promise<void>
	onCancel: () => void
}

const TIME_OFF_TYPES: TimeOffType[] = ['vacation', 'sick_leave', 'unpaid_leave', 'parental_leave', 'other']

const field = (label: string, children: React.ReactNode, error?: string) => (
	<div className='space-y-1.5'>
		<Label className='text-xs text-878787 font-normal'>{label}</Label>
		{children}
		{error && <p className='text-xs text-destructive'>{error}</p>}
	</div>
)

const TimeOffRequestForm = ({ members, showMember = true, initialMemberId = '', onSubmit, onCancel }: Props) => {
	const content = useIntlayer('hr-page')
	const tf = content.timeOff.requestForm

	const [values, setValues] = useState<TimeOffFormValues>({
		memberId: initialMemberId,
		type: 'vacation',
		startDate: '',
		endDate: '',
		halfDay: null,
		reason: '',
	})
	const [errors, setErrors] = useState<Partial<Record<string, string>>>({})
	const [submitting, setSubmitting] = useState(false)

	const set = (key: keyof TimeOffFormValues) => (e: React.ChangeEvent<HTMLInputElement>) =>
		setValues(v => ({ ...v, [key]: e.target.value }))

	const setSelect = (key: keyof TimeOffFormValues) => (value: string) => setValues(v => ({ ...v, [key]: value }))

	const handleSubmit = async (e: React.SubmitEvent<HTMLFormElement>) => {
		e.preventDefault()
		const result = schema.safeParse(values)
		if (!result.success) {
			const { fieldErrors: flat, formErrors } = z.flattenError(result.error)
			const allErrors: Partial<Record<string, string>> = Object.fromEntries(
				Object.entries(flat).map(([k, v]) => [k, v?.[0]]),
			)
			if (formErrors.length > 0) allErrors.endDate = formErrors[0]
			setErrors(allErrors)
			return
		}
		setErrors({})
		setSubmitting(true)
		try {
			await onSubmit(result.data)
		} finally {
			setSubmitting(false)
		}
	}

	return (
		<form onSubmit={handleSubmit}>
			<div className='h-[calc(100vh-180px)] overflow-auto space-y-4'>
				{showMember &&
					field(
						tf.member.value,
						<Select value={values.memberId} onValueChange={setSelect('memberId')}>
							<SelectTrigger>
								<SelectValue placeholder='-' />
							</SelectTrigger>
							<SelectContent>
								{members.map(m => (
									<SelectItem key={m.id} value={m.id}>
										{m.fullName}
									</SelectItem>
								))}
							</SelectContent>
						</Select>,
						errors.memberId ? tf.memberError.value : undefined,
					)}

				{field(
					tf.type.value,
					<Select value={values.type} onValueChange={setSelect('type')}>
						<SelectTrigger>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{TIME_OFF_TYPES.map(t => (
								<SelectItem key={t} value={t}>
									{content.timeOff.requestType[t]?.value ?? t}
								</SelectItem>
							))}
						</SelectContent>
					</Select>,
				)}

				<div className='grid grid-cols-2 gap-4'>
					{field(
						tf.startDate.value,
						<DatePicker value={values.startDate} onChange={v => setValues(prev => ({ ...prev, startDate: v }))} />,
						errors.startDate ? tf.startDateError.value : undefined,
					)}
					{field(
						tf.endDate.value,
						<DatePicker
							value={values.endDate}
							onChange={v => setValues(prev => ({ ...prev, endDate: v }))}
							fromDate={values.startDate ? new Date(values.startDate) : undefined}
						/>,
						errors.endDate,
					)}
				</div>

				{field(
					tf.halfDay.value,
					<Select
						value={values.halfDay ?? 'none'}
						onValueChange={v => setValues(prev => ({ ...prev, halfDay: v === 'none' ? null : (v as 'am' | 'pm') }))}
					>
						<SelectTrigger>
							<SelectValue placeholder='-' />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value='none'>-</SelectItem>
							<SelectItem value='am'>{tf.halfDayAm}</SelectItem>
							<SelectItem value='pm'>{tf.halfDayPm}</SelectItem>
						</SelectContent>
					</Select>,
				)}

				{field(tf.reason.value, <Input value={values.reason ?? ''} onChange={set('reason')} autoComplete='off' />)}
			</div>

			<div className='absolute bottom-0 left-0 right-0 p-4'>
				<div className='flex justify-end space-x-4'>
					<Button variant='outline' onClick={onCancel} type='button'>
						{tf.cancel}
					</Button>
					<SubmitButton isSubmitting={submitting}>{tf.submit}</SubmitButton>
				</div>
			</div>
		</form>
	)
}

export { TimeOffRequestForm }
export type { TimeOffFormValues }
